import { useState, useRef, useEffect } from 'react';
import { useTemplates, useBulkAssign, useSearchUsers } from '../hooks';
import type { UserResult } from '../types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

interface BulkAssignModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialTemplateId?: string;
}

export function BulkAssignModal({ open, onOpenChange, initialTemplateId }: BulkAssignModalProps) {
    const { data: templates, isLoading: templatesLoading } = useTemplates();
    const bulkAssign = useBulkAssign();

    const [templateId, setTemplateId] = useState('');
    const [startDate, setStartDate] = useState(
        new Date().toISOString().slice(0, 10)
    );

    useEffect(() => {
        if (open && initialTemplateId) {
            setTemplateId(initialTemplateId);
        } else if (!open && !bulkAssign.isPending) {
             setTemplateId(''); // reset on close
        }
    }, [open, initialTemplateId, bulkAssign.isPending]);

    // ── User picker state ──
    const [userSearch, setUserSearch] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<
        { id: string; name: string; email: string }[]
    >([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const { data: userResults, isLoading: usersLoading } =
        useSearchUsers(userSearch, open);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node)
            ) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const addUser = (user: UserResult) => {
        if (!selectedUsers.find((u) => u.id === user.id)) {
            setSelectedUsers((prev) => [
                ...prev,
                { id: user.id, name: user.name, email: user.email },
            ]);
        }
        setUserSearch('');
        setDropdownOpen(false);
        inputRef.current?.focus();
    };

    const removeUser = (id: string) => {
        setSelectedUsers((prev) => prev.filter((u) => u.id !== id));
    };

    const handleSubmit = () => {
        if (!templateId) {
            toast.error('Select a template');
            return;
        }
        if (selectedUsers.length === 0) {
            toast.error('Select at least one user');
            return;
        }
        if (!startDate) {
            toast.error('Choose a start date');
            return;
        }

        bulkAssign.mutate(
            {
                template_id: templateId,
                subject_user_ids: selectedUsers.map((u) => u.id),
                start_date: startDate,
            },
            {
                onSuccess: () => {
                    onOpenChange(false);
                    setTemplateId('');
                    setSelectedUsers([]);
                    setUserSearch('');
                },
            }
        );
    };

    // Filter out already-selected users from results
    const filteredResults = (userResults ?? []).filter(
        (u) => !selectedUsers.find((s) => s.id === u.id)
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Assign Checklist</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Template picker */}
                    <div>
                        <label className="mb-1 block text-sm font-medium">Template</label>
                        {templatesLoading ? (
                            <p className="text-sm text-muted-foreground">Loading…</p>
                        ) : (
                            <Select value={templateId} onValueChange={setTemplateId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select template" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(templates ?? [])
                                        .filter((t) => t.is_active)
                                        .map((t) => (
                                            <SelectItem key={t.id} value={t.id}>
                                                {t.name} (v{t.version})
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    {/* User search & picker */}
                    <div ref={dropdownRef}>
                        <label className="mb-1 block text-sm font-medium">
                            Assign to
                        </label>

                        {/* Selected user chips */}
                        {selectedUsers.length > 0 && (
                            <div className="mb-2 flex flex-wrap gap-1">
                                {selectedUsers.map((u) => (
                                    <span
                                        key={u.id}
                                        className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium"
                                    >
                                        {u.name}
                                        <button
                                            type="button"
                                            onClick={() => removeUser(u.id)}
                                            className="ml-0.5 text-muted-foreground hover:text-destructive"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Search input */}
                        <Input
                            ref={inputRef}
                            value={userSearch}
                            onChange={(e) => {
                                setUserSearch(e.target.value);
                                setDropdownOpen(true);
                            }}
                            onFocus={() => {
                                setDropdownOpen(true);
                            }}
                            placeholder="Search by name or email…"
                        />

                        {/* Dropdown results */}
                        {dropdownOpen && (
                            <div className="mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-md">
                                {userSearch.trim().length < 2 ? (
                                    <p className="px-3 py-2 text-sm text-muted-foreground">
                                        Type at least 2 characters…
                                    </p>
                                ) : usersLoading ? (
                                    <p className="px-3 py-2 text-sm text-muted-foreground">
                                        Searching…
                                    </p>
                                ) : filteredResults.length > 0 ? (
                                    filteredResults.map((u) => (
                                        <button
                                            key={u.id}
                                            type="button"
                                            className="flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-muted"
                                            onClick={() => addUser(u)}
                                        >
                                            {u.image_url ? (
                                                <img
                                                    src={u.image_url}
                                                    alt=""
                                                    className="h-7 w-7 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-medium">
                                                    {u.name.charAt(0)}
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium">
                                                    {u.name}
                                                </p>
                                                <p className="truncate text-xs text-muted-foreground">
                                                    {u.email}
                                                </p>
                                            </div>
                                        </button>
                                    ))
                                ) : (
                                    <p className="px-3 py-2 text-sm text-muted-foreground">
                                        No users found
                                    </p>
                                )}
                            </div>
                        )}

                        <p className="mt-1 text-xs text-muted-foreground">
                            {selectedUsers.length} user(s) selected
                        </p>
                    </div>

                    {/* Start date */}
                    <div>
                        <label className="mb-1 block text-sm font-medium">Start Date</label>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={bulkAssign.isPending}>
                        {bulkAssign.isPending ? 'Assigning…' : `Assign (${selectedUsers.length})`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}